"use client"

import { useState } from "react"
import { UserPlus, UserMinus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { followUser, unfollowUser } from "@/lib/db"
import { useToast } from "@/hooks/useToast"

interface FollowButtonProps {
  userId: string
  currentUserId: string
  initialFollowing?: boolean
  variant?: "default" | "outline"
  size?: "sm" | "default" | "lg"
}

export function FollowButton({ userId, currentUserId, initialFollowing = false, variant = "default", size = "default" }: FollowButtonProps) {
  const [isFollowing, setIsFollowing] = useState(initialFollowing)
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  const handleFollow = async () => {
    if (!currentUserId) {
      toast({
        title: "Sign in required",
        description: "Please sign in to follow users",
        variant: "destructive",
      })
      return
    }

    setIsLoading(true)

    try {
      if (isFollowing) {
        await unfollowUser(currentUserId, userId)
        setIsFollowing(false)
        toast({ title: "Unfollowed successfully" })
      } else {
        await followUser(currentUserId, userId)
        setIsFollowing(true)
        toast({ title: "Following!" })
      }
    } catch (_error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Button variant={isFollowing ? "outline" : variant} size={size} onClick={handleFollow} disabled={isLoading} className="w-full sm:w-auto">
      {isFollowing ? (
        <>
          <UserMinus className="mr-2 h-4 w-4" />
          Following
        </>
      ) : (
        <>
          <UserPlus className="mr-2 h-4 w-4" />
          Follow
        </>
      )}
    </Button>
  )
}
